/* eslint-disable */
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, CancelTokenSource, InternalAxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';
import { ICancelablePromise, IEnhancedConfig } from '../../models/httpModels';
import { captureContentUpdate } from '../../utils/oneDsUtil';

// We cannot pull the EMS session id from redux due to a circular include dependency.
// Store it as a local field which is set from redux on this singleton elsewhere.
// Circular dependency detected: ..\web-legacy\appStore.ts -> ..\web-legacy\models\reduxState.ts -> ..\web-legacy\utilities.ts -> utils\getHelputils.ts -> utils\stringLoader.ts -> common\apis\httpUtils.ts -> ..\web-legacy\appStore.ts
export const emsSessionIdHeaderKey: string = 'x-ems-sessionid';
const includedHostnames: string[] = [
  'support.microsoft.com',
  'support-uat.microsoft.com',
  'support.office.com',
  'emerald-ppe.azurewebsites.net',
  'emerald-prod.azurewebsites.net',
  'emerald-ppe.microsoft.com',
  'emerald.microsoft.com',
];

class HttpUtils {
  private client: AxiosInstance;
  private cancelationTokenMap: Map<string, CancelTokenSource>;
  private emsSessionId: string;
  private __RequestVerificationToken: string;

  public constructor() {
    const timeout = 60000;
    this.__RequestVerificationToken = (document.querySelector('input[name="__RequestVerificationToken"]') as HTMLInputElement)?.value || '';
    let config: AxiosRequestConfig;
    const baseUrl: string = window.emeraldContext ? window.emeraldContext.apiEndpoint : '';
    if (!!baseUrl) {
      config = { timeout: timeout, baseURL: baseUrl };
    } else {
      config = { timeout: timeout };
    }

    this.client = axios.create(config);
    this.cancelationTokenMap = new Map<string, CancelTokenSource>();
    this.emsSessionId = '';

    this.client.interceptors.request.use(
      (innerConfig: InternalAxiosRequestConfig) => {
        innerConfig = this._requestInterceptor(innerConfig)
        return innerConfig;
      },
      (error: Error) => {
        return Promise.reject(error);
      }
    );

    this.client.interceptors.response.use(
      (response: AxiosResponse<any>) => {
        response = this._responseInterceptor(response);
        return response;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this._createResponseInterceptors();
  }

  public get<T>(
    url: string,
    config?: AxiosRequestConfig,
    // eslint-disable-next-line @typescript-eslint/ban-types
    customHeader?: {},
    getStatus?: boolean,
    retries?: number
  ): ICancelablePromise<T> {
    const { axiosRequestConfig, cancelTokenId }: IEnhancedConfig = this._enhanceRequestConfig(config, customHeader);

    const start: Date = new Date();
    let end: Date = new Date();
    const localClient: AxiosInstance = retries ? this._getRetryClientCopy(retries) : this.client;
    const axiosPromise: Promise<any> = localClient
      .get(url, axiosRequestConfig)
      .then((response: AxiosResponse<T>): any => {
        end = new Date();
        this._logApiData(
          439906,
          `Perf counter in milliseconds for url: ${url}`,
          'HttpMethod.GET',
          url,
          end.getTime() - start.getTime()
        );
        return this._onSuccessWithCancelToken(cancelTokenId, response, getStatus);
      })
      .catch((reason: AxiosError) => {
        end = new Date();
        this._logApiData(
          439907,
          `Perf counter in milliseconds for url: ${url}`,
          'HttpMethod.GET',
          url,
          end.getTime() - start.getTime(),
          `Error: ${reason.message}`
        );
        return this._onReject(url, reason, cancelTokenId);
      });

    return this._extendPromiseToBeCancelable(axiosPromise, cancelTokenId);
  }

  public post<T>(
    url: string,
    data: Object,
    customHeader?: {},
    config?: AxiosRequestConfig,
    getStatus?: boolean,
    retries?: number
  ): ICancelablePromise<T> {
    const { axiosRequestConfig, cancelTokenId }: IEnhancedConfig = this._enhanceRequestConfig(config, customHeader);

    const start: Date = new Date();
    let end: Date = new Date();
    const localClient: AxiosInstance = retries ? this._getRetryClientCopy(retries) : this.client;

    const axiosPromise: Promise<T> = localClient
      .post(url, data, axiosRequestConfig)
      .then((response: AxiosResponse<T>): any => {
        end = new Date();
        this._logApiData(
          439906,
          `Perf counter in milliseconds for url: ${url}`,
          'HttpMethod.POST',
          url,
          end.getTime() - start.getTime()
        );
        return this._onSuccessWithCancelToken(cancelTokenId, response, getStatus);
      })
      .catch((reason: AxiosError) => {
        end = new Date();
        this._logApiData(
          439907,
          `Perf counter in milliseconds for url: ${url}`,
          'HttpMethod.POST',
          url,
          end.getTime() - start.getTime(),
          `Error: ${reason.message}`
        );
        return this._onReject(url, reason, cancelTokenId);
      });

    return this._extendPromiseToBeCancelable(axiosPromise, cancelTokenId);
  }

  public patch<T>(url: string, data: T, customHeader?: {}, config?: AxiosRequestConfig, retries?: number): Promise<T> {
    const { axiosRequestConfig }: IEnhancedConfig = this._enhanceRequestConfig(config, customHeader);
    const localClient: AxiosInstance = retries ? this._getRetryClientCopy(retries) : this.client;
    return localClient.patch(url, data, axiosRequestConfig).then(this._onSuccess).catch(this._onReject.bind(this, url));
  }

  public put<T>(url: string, data: Object, retries?: number): Promise<T> {
    const localClient: AxiosInstance = retries ? this._getRetryClientCopy(retries) : this.client;
    return localClient.put(url, data).then(this._onSuccess).catch(this._onReject.bind(this, url));
  }

  public delete<T>(url: string, config?: AxiosRequestConfig, retries?: number): Promise<T> {
    const localClient: AxiosInstance = retries ? this._getRetryClientCopy(retries) : this.client;
    return localClient.delete(url, config).then(this._onSuccess).catch(this._onReject.bind(this, url));
  }

  public cancelRequest(cancelTokenId: string, message?: string): Promise<void> {
    return Promise.resolve(this._getTokenFromMapAndCancel(cancelTokenId, message));
  }

  public cancelAllRequests(cancelTokenIds: string[], genericMessage?: string): Promise<void[]> {
    return Promise.all(
      cancelTokenIds.map((cts: string) => {
        return this.cancelRequest(cts, genericMessage);
      })
    );
  }

  public downloadFileWithAnchor(url: string, fileName: string, anchorRef: React.RefObject<HTMLAnchorElement>): void {
    /*
     * Note: I am using the passed in fileName here because the Content-Disposition header
     * isn't available in the Axios response without changes on the server side to make
     * it visible.  The actual filename in the Content-Disposition header is just the
     * attachmentId instead of the filename.
     */
    this.client
      .get(url)
      .then((response: AxiosResponse) => {
        if (response === null || response.data === null) {
          this._logApiData(
            503470,
            `download file '${fileName}' from '${url}' failed: Empty response came back from server`
          );
          return;
        }

        try {
          const blob: Blob = new Blob([response.data], { type: 'application/octet-stream' });
          if (anchorRef.current !== null) {
            anchorRef.current.href = URL.createObjectURL(blob);
            anchorRef.current.download = fileName;
            anchorRef.current.click();
          }
        } catch (err) {
          this._logApiData(503799, `download file '${fileName}' from '${url}' failed:  '${err.message}'`);
        }
      })
      .catch((reason: Error) => {
        this._logApiData(503800, `download file '${fileName}' from '${url}' failed:  '${reason.message}'`);
      });
  }

  public setEmsSessionId(emsSessionId: string) {
    if(!this.emsSessionId && !!emsSessionId) {
      this.emsSessionId = emsSessionId;
    }
  }

  public getEmsSessionId() {
    return this.emsSessionId;
  }

  private _onSuccessWithCancelToken<T>(
    cancelTokenId: string,
    response: AxiosResponse<T>,
    getStatus?: boolean
  ): Promise<{ status: number; data: T }> | Promise<T> {
    this.cancelationTokenMap.delete(cancelTokenId);
    const data: T = this._onSuccess(response);
    return getStatus ? Promise.resolve({ status: response.status, data: response.data }) : Promise.resolve(data);
  }

  private _onSuccess<T>(response: AxiosResponse<T>): T {
    return response.data;
  }

  private _onReject(url: string, error: AxiosError, cancelTokenId?: string): Promise<never> {
    console.log(`Reject on then: ${url}: reason: ${error.message}`);
    // if cancelTokenId is defined, remove it now that the request is complete
    if (cancelTokenId) {
      this.cancelationTokenMap.delete(cancelTokenId);
    }

    // If authorization token is there,
    // remove it from error so it does not get logged in down flow
    if (!!error && !!error.config && !!error.config.headers && !!error.config.headers.Authorization) {
      delete error.config.headers.Authorization;
    }

    return Promise.reject(error);
  }

  private _getTokenFromMapAndCancel(cancelTokenId: string, message?: string): void {
    if (this.cancelationTokenMap.has(cancelTokenId) === true) {
      (this.cancelationTokenMap.get(cancelTokenId) as CancelTokenSource).cancel(message);
    }
  }

  private _extendPromiseToBeCancelable<T>(axiosPromise: Promise<T>, cancelTokenId: string): ICancelablePromise<T> {
    const cancelableAxiosPromise: ICancelablePromise<T> = axiosPromise as ICancelablePromise<T>;
    cancelableAxiosPromise.cancelTokenId = cancelTokenId;
    return cancelableAxiosPromise;
  }

  private _enhanceRequestConfig(config?: AxiosRequestConfig, customHeader?: {}): IEnhancedConfig {
    config = config || {};

    if (customHeader) {
      config.headers = customHeader;
    } else {
      config.headers = { 'Content-Type': 'application/json' };
    }
    config.headers = { ...config.headers, 'X-CSRF-TOKEN': this.__RequestVerificationToken };

    // generated randomId to track the specific cts for a given request/promise.
    let randomId: string = this._generateRandomId();

    // ensure that the randomId is unique
    while (this.cancelationTokenMap.has(randomId)) {
      randomId = this._generateRandomId();
    }

    const cts: CancelTokenSource = this._getCancelTokenSource();

    // add a map to cancelTokenMap for randomId -> cts
    this.cancelationTokenMap.set(randomId, cts);
    config.cancelToken = cts.token;
    return {
      axiosRequestConfig: config,
      cancelTokenId: randomId,
    };
  }

  private _getCancelTokenSource(): CancelTokenSource {
    return axios.CancelToken.source();
  }

  private _generateRandomId(): string {
    return Math.random().toString(36).substring(2) + new Date().getTime().toString(36);
  }

  private _createResponseInterceptors(): any {}

  private _logApiData = (tagId: number, message: string, ...args: any[]) => {
    const data = {
      tagId: tagId,
      message: message,
    };
    if (!!args) {
      data['adhoc0'] = args[0];
      data['adhoc1'] = args[1];
      data['adhoc2'] = args[2];
    }
    const logHandlerTags = {
      content: data,
      behavior: 'OTHER',
    };
    captureContentUpdate(logHandlerTags);
  };

  private _getRetryClientCopy = (retries: number) => {
    const clientCopy: AxiosInstance = axios.create(this.client.defaults);
    axiosRetry(clientCopy, { retries: retries, retryCondition: _error => true });
    return clientCopy;
  };

  private _requestInterceptor = (innerConfig: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    if(!this.emsSessionId)
    {
      // session id isn't set, so do nothing
      return innerConfig;
    }

    // The header should be included in any request that doesn't contain an absolute
    // url.  Otherwise, if it is an absolute url, only include the header if the host
    // is in the list of included hosts.
    let shouldAddHeader = true;
    const isAbsoluteUrl = !!innerConfig.url && innerConfig.url.substring(0, 4) == 'http';
    if(isAbsoluteUrl) {
      const url = new URL(innerConfig.url);
      shouldAddHeader = includedHostnames.filter((h) => h == url.host).length > 0;
    }

    if(shouldAddHeader) {
      innerConfig.headers[emsSessionIdHeaderKey] = this.emsSessionId;
    }

    return innerConfig;
  };

  private _responseInterceptor = (response: AxiosResponse<any>) => {
    if(!!this.emsSessionId) {
      // emsSessionId is already set
      return response;
    }

    if(!response.headers[emsSessionIdHeaderKey]) {
      // no X-Ems-SessionId header in the response
      return response;
    }

    // Now, store the session id for a relative url or an absolute url in the allowed list
    let storeSessionId = true;
    const isAbsoluteUrl = !!response.config.url && response.config.url.substring(0, 4) == 'http';
    if(isAbsoluteUrl) {
      const url = new URL(response.config.url);
      // Check to see if this URL is allowed to set the X-Ems-SessionId
      storeSessionId = includedHostnames.filter((h) => h == url.host).length > 0;
    }

    if(storeSessionId)
    {
      this.setEmsSessionId(response.headers[emsSessionIdHeaderKey]);
    }

    return response;
  };
}

export const httpUtils: HttpUtils = new HttpUtils();
